import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { 
  Bot, 
  BarChart3, 
  FileText, 
  Link2, 
  TrendingUp,
  Calendar,
  CheckCircle,
  Clock,
  ArrowUpRight,
  ArrowDownRight,
  ArrowRight,
  AlertCircle,
  Target,
  Info,
  RefreshCw
} from "lucide-react";
import { toast } from "sonner";
import { useSEOScore } from "@/hooks/useSEOScore";
import { useBlogPostMetrics } from "@/hooks/useBlogPostMetrics";
import { useBacklinksMetrics } from "@/hooks/useBacklinksMetrics";
import { usePageSpeedMetrics } from "@/hooks/usePageSpeedMetrics";
import { getScoreColor, getGradeColor } from "@/lib/seoScoreCalculator";
import { logger } from "@/lib/logger";

import type { User } from "@supabase/supabase-js";

interface ProfileData {
  organization_name: string;
  industry: string;
}

interface WebsiteData {
  id: string;
  domain: string;
  title: string;
  favicon_url: string;
}

interface DashboardOverviewProps {
  user: User;
  profile: ProfileData;
  website: WebsiteData;
  isActive?: boolean;
}

const STATUS_MESSAGES = [
  "Gathering comprehensive website data...",
  "Pinging Website...",
  "Checking if Robots.txt Allows Access...",
  "Generating Backlinks...",
  "Analyzing your business model...",
  "Detecting industry-specific keywords...",
  "Identifying content opportunities...",
  "Building your SEO profile...",
  "Preparing personalized insights..."
];

// Hero Section Component
const HeroSection = ({ onGenerateClick }: { onGenerateClick: () => void }) => (
  <Card className="
    w-full 
    p-10 
    flex flex-col md:flex-row items-center gap-12 
    bg-gradient-to-br from-primary/10 via-primary/5 to-background 
    border border-primary/20 
    rounded-[2.5rem]
    shadow-[0_8px_30px_rgb(0,0,0,0.04)]
    hover:shadow-[0_8px_40px_rgba(59,130,246,0.12)]
    transition-all duration-500 ease-out
    backdrop-blur-xl
    overflow-hidden
    relative
    group
  ">
    <div className="absolute top-0 right-0 w-[300px] h-[300px] bg-primary/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3 pointer-events-none"></div>
    <div className="absolute bottom-0 left-0 w-[200px] h-[200px] bg-blue-400/10 rounded-full blur-3xl translate-y-1/3 -translate-x-1/3 pointer-events-none"></div>

    <div className="flex-1 text-center md:text-left space-y-6 relative z-10">
      <h2 className="text-3xl md:text-4xl font-bold text-gray-900 leading-tight">
        Turn content into customers with AI powered content!
      </h2>
      <p className="text-lg text-gray-600 leading-relaxed max-w-2xl">Generate high-ranking blog posts that drive organic traffic and sign-ups with zero manual effort required.</p>
      <div className="flex flex-wrap gap-4 justify-center md:justify-start pt-2">
        <Button 
          onClick={onGenerateClick}
          className="
          rounded-full 
          px-10 py-6 
          text-lg font-bold 
          bg-gradient-to-r from-primary via-primary to-primary/90 text-primary-foreground 
          hover:from-primary/95 hover:via-primary/90 hover:to-primary/85 
          transition-all duration-300 ease-out
          shadow-lg shadow-primary/25
          hover:scale-105
          min-h-[60px]
          relative overflow-hidden group
        ">
          <span className="relative z-10 font-bold tracking-wide">Generate Content</span>
          <ArrowRight className="h-5 w-5 ml-2 group-hover:translate-x-1 transition-transform duration-200 relative z-10" />
          <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/10 to-white/0 transform translate-x-[-100%] group-hover:translate-x-0 transition-transform duration-500"></div>
        </Button>
      </div>
    </div>
    <div className="flex-shrink-0 relative z-10">
      <div className="w-56 h-56 rounded-[2rem] bg-gradient-to-br from-white/80 to-white/40 dark:from-black/20 dark:to-black/5 border border-white/50 dark:border-white/10 shadow-2xl flex items-center justify-center p-6 backdrop-blur-md transform transition-transform duration-700 group-hover:scale-105 group-hover:rotate-2">
        <img 
          src="https://ancujowsxivyfnxhnsmr.supabase.co/storage/v1/object/public/images/Untitled-222.png" 
          alt="Seoro AI Logo" 
          className="w-full h-full object-contain drop-shadow-xl filter brightness-105"
        />
      </div>
    </div>
  </Card>
);

// Metric Card Component
interface MetricCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ReactNode;
  status?: string;
  statusIcon?: React.ReactNode;
}

const MetricCard = ({ title, value, subtitle, icon, status, statusIcon }: MetricCardProps) => (
  <Card className="
    bg-background/95
    backdrop-blur-md
    border border-border/40
    shadow-[0_2px_12px_rgba(0,0,0,0.04),0_1px_4px_rgba(0,0,0,0.02)]
    hover:shadow-[0_4px_20px_rgba(0,0,0,0.06),0_2px_8px_rgba(0,0,0,0.03)]
    rounded-3xl
    transition-all duration-300 ease-out
  ">
    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4 pt-6 px-6">
      <CardTitle className="text-sm font-semibold text-muted-foreground tracking-wide">{title}</CardTitle>
      <div className="text-primary/80 p-3 rounded-2xl bg-primary/10 border border-primary/20 shadow-[0_1px_3px_rgba(0,0,0,0.1)]">{icon}</div>
    </CardHeader>
    <CardContent className="px-6 pb-6">
      <div className="text-3xl font-bold text-foreground mb-2">{value}</div>
      {subtitle && (
        <p className="text-sm text-muted-foreground font-medium">{subtitle}</p>
      )}
      {status && (
        <div className="flex items-center space-x-2 text-sm text-muted-foreground mt-2">
          {statusIcon}
          <span className="font-medium">{status}</span>
        </div>
      )}
    </CardContent>
  </Card>
);

// Performance Chart Component
interface PerformanceChartProps {
  title: string;
  value: string | number;
  change: number;
  chartData: any[];
  gradientId: string;
  strokeColor: string;
  stopColor: string;
  hoverActive: boolean;
  setHoverActive: (active: boolean) => void;
}

const PerformanceChart = ({ 
  title, 
  value, 
  change, 
  chartData, 
  gradientId, 
  strokeColor, 
  stopColor, 
  hoverActive, 
  setHoverActive 
}: PerformanceChartProps) => {
  // Determine the data key based on the title
  const dataKey = title.toLowerCase() === 'clicks' ? 'clicks' : 'impressions';
  const [hoveredPoint, setHoveredPoint] = useState<{ x: number; y: number; value: number; date: string } | null>(null);

  // Generate ultra-smooth, flowing curves like water
  const generateFlowingPath = (data: any[], dataKey: string) => {
    if (!data || data.length === 0) {
      // Beautiful fallback curve
      return "M0,100 C90,95 180,105 270,98 C360,92 450,102 540,96";
    }
    
    // Use all data points for complete 7-day display
    const simplifiedData = data;
    
    const rawValues = simplifiedData.map(d => Number(d[dataKey]) || 0);
    const maxValue = Math.max(...rawValues);
    const minValue = Math.min(...rawValues);
    const allZero = maxValue === 0 && minValue === 0;
    const range = Math.max(1, maxValue - minValue);

    // Log-scale transform to emphasize small variations while keeping zeros visible
    const logValues = rawValues.map(v => Math.log1p(Math.max(0, v))); // log(1+v)
    const maxLog = Math.max(...logValues);
    const minLog = Math.min(...logValues);
    const logRange = Math.max(1e-6, maxLog - minLog);
    
    const width = 400;
    const height = 140;
    const padding = 30; // More padding for gentler curves
    
    // Convert to smooth points with reduced variation
    const points = simplifiedData.map((item, index) => {
      const x = (index / (simplifiedData.length - 1)) * width;
      let value = Number(item[dataKey]) || 0;

      // Adaptive scaling to avoid visually flat lines when values are tiny
      // If values are very small or all zeros, lift them to a visible band
      let normalizedValue = (Math.log1p(Math.max(0, value)) - minLog) / logRange;
      if (!Number.isFinite(normalizedValue)) normalizedValue = 0;

      // Emphasize subtle changes when traffic is very low
      if (maxValue <= 5) {
        // Map 0..1 to 0.3..1 for visibility when traffic is tiny
        normalizedValue = 0.3 + normalizedValue * 0.7;
      } else if (maxValue <= 20) {
        // Slight lift for low ranges
        normalizedValue = 0.15 + normalizedValue * 0.85;
      }
      
      // Smooth out extreme variations
      normalizedValue = Math.pow(normalizedValue, 0.7); // Soften the curve
      
      const y = height - padding - (normalizedValue * (height - padding * 2));
      return { 
        x, 
        y, 
        value, 
        date: item.date || `Day ${index + 1}` 
      };
    });
    
    // If all values are zero, show a gentle placeholder curve instead of a hard baseline
    if (allZero) {
      return {
        path: `M0,110 C80,105 160,112 240,108 C320,104 400,110 400,110`,
        points
      };
    }

    if (points.length < 2) return `M0,${points[0]?.y || 100}`;
    
    // Create flowing water-like curves
    let path = `M${points[0].x},${points[0].y}`;
    
    for (let i = 1; i < points.length; i++) {
      const prev = points[i - 1];
      const curr = points[i];
      
      // Ultra-smooth control points
      const tension = 0.8; // Maximum smoothness
      const dx = curr.x - prev.x;
      const dy = curr.y - prev.y;
      
      // Create gentle, flowing control points
      const cp1x = prev.x + dx * tension * 0.4;
      const cp1y = prev.y + dy * tension * 0.1; // Minimal vertical control
      const cp2x = curr.x - dx * tension * 0.4;
      const cp2y = curr.y - dy * tension * 0.1;
      
      path += ` C${cp1x},${cp1y} ${cp2x},${cp2y} ${curr.x},${curr.y}`;
    }
    
    return { path, points };
  };


  const { strokePath, fillPath, dataPoints } = useMemo(() => {
    const result = generateFlowingPath(chartData, dataKey);
    const path = typeof result === 'string' ? result : result.path;
    const points = typeof result === 'object' ? result.points : [];
    return {
      strokePath: path,
      fillPath: path + ` L400,140 L0,140 Z`,
      dataPoints: points
    };
  }, [chartData, dataKey]);

  const handleMouseMove = (event: React.MouseEvent) => {
    if (!dataPoints.length) return;

    const rect = event.currentTarget.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * 400;
    const y = ((event.clientY - rect.top) / rect.height) * 140;

    // Find closest data point
    const closest = dataPoints.reduce((prev, curr) =>
      Math.abs(curr.x - x) < Math.abs(prev.x - x) ? curr : prev
    );

    setHoveredPoint(closest);
    setHoverActive(true);
  };

  const handleMouseLeave = () => {
    setHoveredPoint(null);
    setHoverActive(false);
  };

  return (
    <Card className="
      overflow-visible 
      rounded-3xl 
      shadow-[0_3px_16px_rgba(0,0,0,0.04),0_1px_6px_rgba(0,0,0,0.02)] 
      border border-border/30
      bg-background/90
      backdrop-blur-lg
    ">
      <CardHeader className="pb-0 pt-6 px-6">
        <div className="flex items-start justify-between">
          <CardTitle className="text-xs uppercase tracking-wide text-gray-400 font-medium">{title}</CardTitle>
          <div className="flex items-center gap-2">
            {change > 0 ? (
              <ArrowUpRight className="h-4 w-4 text-emerald-500" />
            ) : change < 0 ? (
              <ArrowDownRight className="h-4 w-4 text-rose-500" />
            ) : (
              <div className="h-4 w-4 rounded-full bg-gray-300"></div>
            )}
            <span className={`text-xs font-medium ${
              change > 0 ? 'text-emerald-600' : change < 0 ? 'text-rose-600' : 'text-gray-500'
            }`}>
              {change === 0 && value === 0 ? 'No data yet' : `${Math.abs(change)}%`}
            </span>
            <span className="text-xs text-gray-400">
              {change === 0 && value === 0 ? 'New site' : 'Last week'}
            </span>
          </div>
        </div>
        <div className="mt-4">
          <span className="block text-5xl md:text-6xl font-bold text-gray-900 leading-tight">
            {typeof value === 'number' ? value.toLocaleString() : value}
          </span>
        </div>
      </CardHeader>
      <CardContent className="pt-4 pb-6 px-6">
        <div
          className="h-[160px] w-full rounded-2xl bg-gradient-to-br from-gray-50/60 to-gray-50/30 overflow-visible relative cursor-pointer border border-border/20"
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
          style={{ position: 'relative', isolation: 'isolate' }}
        >
          <svg className="absolute inset-0 w-full h-full" viewBox="0 0 400 140" preserveAspectRatio="none">
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={stopColor} stopOpacity="0.12" />
                <stop offset="100%" stopColor={stopColor} stopOpacity="0.00" />
              </linearGradient>
            </defs>
            
            {/* Subtle fill */}
            <path d={fillPath} fill={`url(#${gradientId})`} />
            
            {/* Main flowing line */}
            <path 
              d={strokePath} 
              stroke={strokeColor} 
              strokeWidth="2.5" 
              fill="none" 
              strokeLinecap="round"
              className="drop-shadow-sm"
            />

            
            {/* Modern hover indicator */}
            {hoveredPoint && (
              <g>
                {/* Subtle background highlight */}
                <rect
                  x={hoveredPoint.x - 20}
                  y="0"
                  width="40"
                  height="140"
                  fill={strokeColor}
                  fillOpacity="0.04"
                />
                {/* Clean data point indicator */}
                <circle
                  cx={hoveredPoint.x}
                  cy={hoveredPoint.y}
                  r="2"
                  fill={strokeColor}
                />
                {/* Outer ring for emphasis */}
                <circle
                  cx={hoveredPoint.x}
                  cy={hoveredPoint.y}
                  r="4"
                  fill="none"
                  stroke={strokeColor}
                  strokeWidth="1"
                  strokeOpacity="0.2"
                />
              </g>
            )}
          </svg>
          
          {/* Smart tooltip with boundary detection */}
          {hoveredPoint && (
            <div
              className="absolute pointer-events-none z-[99999]"
              style={{
                left: `${(hoveredPoint.x / 400) * 100}%`,
                top: `${Math.max((hoveredPoint.y / 140) * 100 - 15, 5)}%`,
                transform: 'translate(-50%, -100%)'
              }}
            >
              <div className="bg-white rounded-lg px-4 py-3 shadow-2xl border border-gray-200 relative">
                {/* Small pointer arrow */}
                <div
                  className="absolute w-0 h-0 border-l-[6px] border-r-[6px] border-t-[6px] border-transparent border-t-white"
                  style={{
                    left: '50%',
                    top: '100%',
                    transform: 'translateX(-50%)'
                  }}
                />
                <div className="flex flex-col gap-1">
                  <div className="text-xs font-medium text-gray-500">
                    {(() => {
                      // Ensure date is formatted as YYYY-MM-DD; if time slips in, strip it
                      const raw = hoveredPoint.date || '';
                      const d = String(raw).slice(0, 10);
                      return d;
                    })()}
                  </div>
                  <div className="text-sm font-semibold text-gray-900">
                    {title}: {Number(hoveredPoint.value || 0).toLocaleString()}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// Mock Performance Chart Component
interface MockPerformanceChartProps {
  title: string;
  value: string | number;
  change: number;
  gradientId: string;
  strokeColor: string;
  stopColor: string;
  showRealisticData?: boolean;
}

// Generate ultra-smooth, premium mock data with realistic uptrends and natural curves
const generateMockChartData = (type: 'clicks' | 'impressions') => {
  const days = 90; // More data points for even smoother curves
  const data = [];
  let baseValue = type === 'clicks' ? 120 : 2000; // Lower base for more dramatic growth
  const targetValue = type === 'clicks' ? 2847 : 45231; // Match the displayed values
  const growthTarget = (targetValue / baseValue) - 1; // Calculate required growth

  for (let i = 0; i < days; i++) {
    // Natural growth curve with multiple phases
    const growthProgress = i / (days - 1); // 0 to 1 over time
    
    // Multi-phase growth: slow start, acceleration, then steady growth
    let growthCurve;
    if (growthProgress < 0.3) {
      // Slow initial growth (0-30% of time)
      growthCurve = Math.pow(growthProgress / 0.3, 1.5) * 0.3;
    } else if (growthProgress < 0.7) {
      // Acceleration phase (30-70% of time)
      const phaseProgress = (growthProgress - 0.3) / 0.4;
      growthCurve = 0.3 + Math.pow(phaseProgress, 0.8) * 0.5;
    } else {
      // Steady growth phase (70-100% of time)
      const phaseProgress = (growthProgress - 0.7) / 0.3;
      growthCurve = 0.8 + Math.pow(phaseProgress, 0.6) * 0.2;
    }
    
    const strongUptrend = growthCurve * growthTarget * 0.7;

    // Multiple frequency waves for natural variation
    const primaryWave = Math.sin(i / 12) * 0.04; // Weekly cycle
    const secondaryWave = Math.sin(i / 6) * 0.02; // Bi-weekly cycle
    const tertiaryWave = Math.sin(i / 24) * 0.015; // Monthly cycle
    const quaternaryWave = Math.sin(i / 36) * 0.01; // Longer cycle
    
    // Seasonal effects
    const seasonalEffect = Math.sin(i / 90 * 2 * Math.PI) * 0.02; // Yearly seasonality
    
    // Weekend effects with variation
    const dayOfWeek = i % 7;
    let weekendEffect = 0;
    if (dayOfWeek === 5) weekendEffect = 0.03; // Friday boost
    else if (dayOfWeek === 6) weekendEffect = 0.04; // Saturday boost
    else if (dayOfWeek === 0) weekendEffect = 0.02; // Sunday boost
    else if (dayOfWeek === 1) weekendEffect = -0.01; // Monday dip

    // Momentum that builds and varies
    const momentum = Math.pow(growthProgress, 1.2) * 0.04;
    
    // Volatility that increases with growth (more traffic = more variation)
    const volatility = growthProgress * 0.03;
    const randomVariation = (Math.random() - 0.5) * volatility;
    
    // Type-specific characteristics
    let typeSpecificEffect = 0;
    if (type === 'clicks') {
      // Clicks are more volatile and have stronger weekly patterns
      typeSpecificEffect = Math.sin(i / 7) * 0.015 + Math.sin(i / 14) * 0.008;
    } else {
      // Impressions are more stable but have subtle patterns
      typeSpecificEffect = Math.sin(i / 10) * 0.008 + Math.sin(i / 20) * 0.005;
    }

    // Combine all effects
    const multiplier = 1 + strongUptrend + primaryWave + secondaryWave + tertiaryWave + 
                     quaternaryWave + seasonalEffect + weekendEffect + momentum + 
                     randomVariation + typeSpecificEffect;
    
    const value = Math.max(0, Math.round(baseValue * multiplier));

    data.push({
      date: new Date(Date.now() - (days - i) * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      [type]: value
    });
  }

  return data;
};

export const RealPerformanceChart = ({
  title,
  value,
  change,
  gradientId,
  strokeColor,
  stopColor,
  chartData,
  dataKey
}: MockPerformanceChartProps & { chartData: any[], dataKey: string }) => {
  return (
    <Card className="
      overflow-hidden
      rounded-3xl
      shadow-[0_3px_16px_rgba(0,0,0,0.04),0_1px_6px_rgba(0,0,0,0.02)]
      border border-border/30
      bg-background/90
      backdrop-blur-lg
    ">
      <CardHeader className="pb-2 pt-8 px-8">
        <div className="flex items-start justify-between">
          <CardTitle className="text-sm uppercase tracking-wider text-muted-foreground font-semibold">{title}</CardTitle>
          <div className="flex items-center gap-2">
            {change >= 0 ? (
              <ArrowUpRight className="h-4 w-4 text-green-500" />
            ) : (
              <ArrowDownRight className="h-4 w-4 text-rose-500" />
            )}
            <span className={`text-sm font-bold ${
              change >= 0 ? 'text-green-600' : 'text-rose-600'
            }`}>
              {Math.abs(change)}%
            </span>
            <span className="text-sm text-muted-foreground font-medium">Last week</span>
          </div>
        </div>
        <div className="mt-6">
          <span className="block text-6xl font-bold text-foreground leading-none">{value}</span>
        </div>
      </CardHeader>
      <CardContent className="pt-6 pb-8 px-8">
        <div className="h-[180px] w-full rounded-3xl bg-gradient-to-br from-muted/20 to-muted/5 overflow-hidden relative border border-border/20">
          {showRealisticData && (
            <svg className="absolute inset-0 w-full h-full opacity-90">
              <defs>
                <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={stopColor} stopOpacity="0.25" />
                  <stop offset="100%" stopColor={stopColor} stopOpacity="0.00" />
                </linearGradient>
              </defs>

              {/* Generate ultra-smooth premium flowing chart path */}
              {(() => {
                const maxValue = Math.max(...mockChartData.map(d => d[title.toLowerCase() as keyof typeof d] as number));
                const minValue = Math.min(...mockChartData.map(d => d[title.toLowerCase() as keyof typeof d] as number));
                const range = maxValue - minValue || 1;

                // Smooth the data with advanced moving average for ultra-premium curves
                const smoothedData = mockChartData.map((item, index) => {
                  if (index === 0 || index === mockChartData.length - 1) {
                    return item[title.toLowerCase() as keyof typeof item] as number;
                  }

                  // Apply weighted 5-point moving average for ultra-smoothness
                  const weights = [0.1, 0.2, 0.4, 0.2, 0.1]; // Center-weighted smoothing
                  let weightedSum = 0;
                  let totalWeight = 0;

                  for (let i = -2; i <= 2; i++) {
                    const dataIndex = index + i;
                    if (dataIndex >= 0 && dataIndex < mockChartData.length) {
                      const value = mockChartData[dataIndex][title.toLowerCase() as keyof typeof mockChartData[0]] as number;
                      const weight = weights[i + 2];
                      weightedSum += value * weight;
                      totalWeight += weight;
                    }
                  }

                  return weightedSum / totalWeight;
                });

                const points = smoothedData.map((value, index) => {
                  const x = (index / (smoothedData.length - 1)) * 400;
                  const normalizedValue = (value - minValue) / range;
                  const y = 160 - 25 - (normalizedValue * (160 - 50)); // More padding for premium look
                  return { x, y };
                });

                if (points.length < 2) return null;

                // Generate ultra-smooth bezier curves with natural flow
                let path = `M${points[0].x},${points[0].y}`;

                for (let i = 1; i < points.length; i++) {
                  const prev = points[i - 1];
                  const curr = points[i];

                  if (i === 1) {
                    // First curve - gentle start
                    const cp1x = prev.x + (curr.x - prev.x) * 0.6;
                    const cp1y = prev.y + (curr.y - prev.y) * 0.3;
                    const cp2x = curr.x - (curr.x - prev.x) * 0.3;
                    const cp2y = curr.y - (curr.y - prev.y) * 0.1;
                    path += ` C${cp1x},${cp1y} ${cp2x},${cp2y} ${curr.x},${curr.y}`;
                  } else if (i === points.length - 1) {
                    // Last curve - gentle end
                    const prevPrev = points[i - 2];
                    const cp1x = prev.x + (curr.x - prevPrev.x) * 0.2;
                    const cp1y = prev.y + (curr.y - prevPrev.y) * 0.1;
                    const cp2x = curr.x - (curr.x - prev.x) * 0.4;
                    const cp2y = curr.y - (curr.y - prev.y) * 0.2;
                    path += ` C${cp1x},${cp1y} ${cp2x},${cp2y} ${curr.x},${curr.y}`;
                  } else {
                    // Middle curves - ultra-smooth with natural flow
                    const next = points[i + 1];
                    const prevPrev = points[i - 2];

                    // Calculate direction vectors for smooth transitions
                    const prevDirX = prev.x - prevPrev.x;
                    const prevDirY = prev.y - prevPrev.y;
                    const nextDirX = next.x - curr.x;
                    const nextDirY = next.y - curr.y;

                    // Smooth tension based on curve direction changes
                    const tension = 0.85;
                    const smoothness = 0.6;

                    // Calculate control points with natural flow
                    const cp1x = prev.x + (prevDirX + (curr.x - prev.x)) * smoothness * 0.3;
                    const cp1y = prev.y + (prevDirY + (curr.y - prev.y)) * smoothness * 0.2;
                    const cp2x = curr.x - (nextDirX + (curr.x - prev.x)) * smoothness * 0.3;
                    const cp2y = curr.y - (nextDirY + (curr.y - prev.y)) * smoothness * 0.2;

                    path += ` C${cp1x},${cp1y} ${cp2x},${cp2y} ${curr.x},${curr.y}`;
                  }
                }

                const fillPath = path + ` L400,160 L0,160 Z`;

                return (
                  <>
                    <path d={fillPath} fill={`url(#${gradientId})`} opacity="0.8" />
                    <path
                      d={path}
                      stroke={strokeColor}
                      strokeWidth="3.5"
                      fill="none"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="drop-shadow-sm"
                    />
                    {/* Subtle uptrend indicator line */}
                    <line
                      x1="0"
                      y1="140"
                      x2="400"
                      y2="25"
                      stroke={strokeColor}
                      strokeWidth="1.5"
                      strokeOpacity="0.4"
                      strokeDasharray="6,8"
                    />
                  </>
                );
              })()}
            </svg>
          )}

          {/* Premium fallback chart if no realistic data */}
          {!showRealisticData && (
            <svg className="absolute inset-0 w-full h-full opacity-90">
              <defs>
                <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={stopColor} stopOpacity="0.25" />
                  <stop offset="100%" stopColor={stopColor} stopOpacity="0.00" />
                </linearGradient>
              </defs>
              {/* Ultra-smooth premium fallback curve with natural variation */}
              <path
                d="M0,130 C40,125 80,135 120,128 C160,121 200,129 240,125 C280,121 320,127 360,123 C400,119 440,125 480,121 C520,117 560,123 600,119 C640,115 680,121 720,117 C760,113 800,119 840,115 C880,111 920,117 960,113 C1000,109 1040,115 1080,111 C1120,107 1160,113 1200,109"
                stroke={strokeColor}
                strokeWidth="3.5"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="drop-shadow-sm"
              />
              <path
                d="M0,130 C40,125 80,135 120,128 C160,121 200,129 240,125 C280,121 320,127 360,123 C400,119 440,125 480,121 C520,117 560,123 600,119 C640,115 680,121 720,117 C760,113 800,119 840,115 C880,111 920,117 960,113 C1000,109 1040,115 1080,111 C1120,107 1160,113 1200,109 L1200,160 L0,160 Z"
                fill={`url(#${gradientId})`}
                opacity="0.8"
              />
              {/* Uptrend indicator for fallback */}
              <line
                x1="0"
                y1="140"
                x2="400"
                y2="25"
                stroke={strokeColor}
                strokeWidth="1.5"
                strokeOpacity="0.4"
                strokeDasharray="6,8"
              />
            </svg>
          )}

          <div className="absolute inset-0 bg-gradient-to-t from-background/20 to-transparent backdrop-blur-sm" />
        </div>
      </CardContent>
    </Card>
  );
};

// Content Grid Component
interface ContentGridProps {
  blogPosts: any[];
  analyticsData: any;
  isConnectedToGoogle: boolean;
  connectToGoogle: () => void;
}

const ContentGrid = ({ blogPosts, analyticsData, isConnectedToGoogle, connectToGoogle }: ContentGridProps) => (
  <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
    {/* Recent Blog Posts */}
    <Card className="
      bg-background/95
      backdrop-blur-lg
      border border-border/30
      shadow-[0_3px_16px_rgba(0,0,0,0.04),0_1px_6px_rgba(0,0,0,0.02)]
      hover:shadow-[0_6px_24px_rgba(0,0,0,0.06),0_2px_10px_rgba(0,0,0,0.03)]
      rounded-3xl
      transition-all duration-500 ease-out
    ">
      <CardHeader className="pb-6 pt-8 px-8">
        <CardTitle className="text-2xl font-bold text-foreground tracking-tight">Recent Blog Posts</CardTitle>
      </CardHeader>
      <CardContent className="px-8 pb-8">
        <div className="space-y-6">
          {blogPosts.length === 0 ? (
            <p className="text-muted-foreground text-center py-6 font-medium">No blog posts yet</p>
          ) : (
            blogPosts.slice(0, 2).map((post) => (
              <div key={post.id} className="flex items-start justify-between p-6 rounded-3xl bg-gradient-to-br from-muted/30 to-muted/10 backdrop-blur-md border border-border/20 shadow-[0_2px_8px_rgba(0,0,0,0.02)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.04)] transition-all duration-300">
                <div className="space-y-3">
                  <p className="text-base font-bold text-foreground leading-relaxed">{post.title}</p>
                  <div className="flex items-center space-x-3">
                    <Badge variant={post.generation_status === 'completed' ? 'default' : 'secondary'} className="rounded-full px-3 py-1 text-xs font-semibold">
                      {post.generation_status}
                    </Badge>
                    <span className="text-sm text-muted-foreground font-medium">
                      {new Date(post.created_at).toLocaleDateString()}
                    </span>
                    {post.word_count && (
                      <span className="text-sm text-muted-foreground font-medium">
                        • {post.word_count} words
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </CardContent>
    </Card>

    {/* Additional Analytics */}
    <Card className="
      bg-background/95
      backdrop-blur-lg
      border border-border/30
      shadow-[0_3px_16px_rgba(0,0,0,0.04),0_1px_6px_rgba(0,0,0,0.02)]
      hover:shadow-[0_6px_24px_rgba(0,0,0,0.06),0_2px_10px_rgba(0,0,0,0.03)]
      rounded-3xl
      transition-all duration-500 ease-out
    ">
      <CardHeader className="pb-6 pt-8 px-8">
        <CardTitle className="text-2xl font-bold text-foreground tracking-tight">Additional Metrics</CardTitle>
      </CardHeader>
      <CardContent className="px-8 pb-8">
        <div className="space-y-8">
          {analyticsData ? (
            <>
              <div className="grid grid-cols-1 gap-6">
                <div className="rounded-2xl bg-muted/20 backdrop-blur-sm border border-border/30 p-6">
                  <p className="text-sm uppercase tracking-wider text-muted-foreground font-semibold mb-3">Click-through Rate</p>
                  <div className="flex items-baseline gap-3">
                    <p className="text-4xl font-bold text-foreground">{analyticsData.avgCtr}%</p>
                    <span className="text-sm text-muted-foreground font-medium">Last 7 days</span>
                  </div>
                </div>
                <div className="rounded-2xl bg-muted/20 backdrop-blur-sm border border-border/30 p-6">
                  <p className="text-sm uppercase tracking-wider text-muted-foreground font-semibold mb-3">Average Position</p>
                  <div className="flex items-baseline gap-3">
                    <p className="text-4xl font-bold text-foreground">{analyticsData.avgPosition}</p>
                    <span className="text-sm text-muted-foreground font-medium">Last 7 days</span>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-8">
              {!isConnectedToGoogle ? (
                <p className="text-muted-foreground font-medium">Connect your Google Search Console account to view detailed analytics</p>
              ) : (
                <p className="text-muted-foreground font-medium">Loading analytics data...</p>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  </div>
);

export function DashboardOverview({ user, profile, website, isActive = true }: DashboardOverviewProps) {
  const navigate = useNavigate();
  const [analyticsData, setAnalyticsData] = useState<any>(null);
  const [chartData, setChartData] = useState<any[]>([]);
  const [smoothChartData, setSmoothChartData] = useState<any[]>([]);
  const [avgSmoothChartData, setAvgSmoothChartData] = useState<any[]>([]);
  const [seoAudits, setSeoAudits] = useState<any[]>([]);
  const [blogPosts, setBlogPosts] = useState<any[]>([]);
  const [statusMessage, setStatusMessage] = useState(STATUS_MESSAGES[0]);
  const [analysisStartTime] = useState(Date.now());
  const [isConnectedToGoogle, setIsConnectedToGoogle] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [hoverActive, setHoverActive] = useState<boolean>(false);
  const [hoverPayload, setHoverPayload] = useState<{ label?: string; clicks?: number; impressions?: number } | null>(null);
  const [isSimulatedData, setIsSimulatedData] = useState<boolean>(false);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  const [lastRefreshTime, setLastRefreshTime] = useState<number>(0);
  const [googleConnectionInfo, setGoogleConnectionInfo] = useState<{
    email?: string;
    siteUrl?: string;
    connectedAt?: string;
  } | null>(null);

  // Use the SEO score hook - only fetch when tab is active
  const { seoScore, loading: seoScoreLoading, error: seoScoreError, refreshScore } = useSEOScore(website.id, isActive);
  
  // Use the blog post metrics hook - only fetch when tab is active
  const { totalGenerated, completedArticles, failedArticles, pendingArticles, loading: blogMetricsLoading } = useBlogPostMetrics(website.id, isActive);
  
  // Use the backlinks metrics hook - only fetch when tab is active
  const { totalBacklinks, activeBacklinks, pendingBacklinks, failedBacklinks, last30Days, loading: backlinksLoading } = useBacklinksMetrics(website.id, isActive);
  
  // Use the page speed metrics hook - only fetch when tab is active
  const { score: pageSpeedScore, grade: pageSpeedGrade, loading: pageSpeedLoading } = usePageSpeedMetrics(website.id, isActive);

  // Function to get date range from analytics data
  const getDateRange = (data: any[]) => {
    if (!data || data.length === 0) return null;
    
    const dates = data.map(item => new Date(item.date)).sort((a, b) => a.getTime() - b.getTime());
    const startDate = dates[0];
    const endDate = dates[dates.length - 1];
    
    const formatDate = (date: Date) => {
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric' 
      });
    };
    
    return `${formatDate(startDate)} - ${formatDate(endDate)}, ${endDate.getFullYear()}`;
  };

  // Generic retry helper with exponential backoff for robustness
  const withRetry = async <T,>(operation: () => Promise<T>, description: string, maxAttempts = 3): Promise<T> => {
    let attempt = 0;
    let lastError: unknown = null;
    while (attempt < maxAttempts) {
      try {
        return await operation();
      } catch (error) {
        lastError = error;
        const delayMs = Math.min(1000 * Math.pow(2, attempt), 4000);
        logger.warn(`Retry ${attempt + 1}/${maxAttempts} for ${description} failed:`, error);
        await new Promise((res) => setTimeout(res, delayMs));
        attempt += 1;
      }
    }
    throw lastError instanceof Error ? lastError : new Error(`Failed ${description}`);
  };

  // Cache key for analytics data
  const getCacheKey = (websiteId: string) => `analytics_${websiteId}_v5`;
  const getCacheExpiry = () => 2 * 60 * 1000; // 2 minutes cache for more frequent updates

  // Helper function to get time ago format
  const getTimeAgo = (timestamp: number) => {
    const now = Date.now();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    
    if (minutes < 1) return 'just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  // Clear all analytics caches to ensure fresh data
  const clearAllAnalyticsCaches = () => {
    try {
      // Clear all localStorage items that might contain analytics data
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.includes('analytics')) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
      
      // Clear all sessionStorage items that might contain analytics data
      const sessionKeysToRemove = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key && key.includes('analytics')) {
          sessionKeysToRemove.push(key);
        }
      }
      sessionKeysToRemove.forEach(key => sessionStorage.removeItem(key));
      
      // Also clear any cached data in memory
      setAnalyticsData(null);
      setChartData([]);
      setSmoothChartData([]);
      setAvgSmoothChartData([]);
      
      logger.log('Cleared all analytics caches and memory state');
    } catch (error) {
      logger.error('Error clearing caches:', error);
    }
  };

  // Load Google connection info
  const loadGoogleConnectionInfo = async () => {
    try {
      const { data: settingsData, error } = await supabase
        .from('user_settings')
        .select('google_search_console_email, google_search_console_site_url, google_search_console_connected_at')
        .eq('user_id', user.id)
        .single();

      if (!error && settingsData) {
        setGoogleConnectionInfo({
          email: settingsData.google_search_console_email,
          siteUrl: settingsData.google_search_console_site_url,
          connectedAt: settingsData.google_search_console_connected_at
        });
        setIsConnectedToGoogle(!!settingsData.google_search_console_email);
      }
    } catch (error) {
      logger.error('Error loading Google connection info:', error);
    }
  }; // 5 minutes

  // Check if data is fresh (less than 1 hour old)
  const isDataFresh = (timestamp: number) => {
    const now = Date.now();
    const oneHour = 60 * 60 * 1000;
    return (now - timestamp) < oneHour;
  };

  // Load cached analytics data
  const loadCachedAnalytics = (websiteId: string) => {
    try {
      const cacheKey = getCacheKey(websiteId);
      const cached = sessionStorage.getItem(cacheKey);
      if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        const now = Date.now();
        if (now - timestamp < getCacheExpiry()) {
          return data;
        }
      }
    } catch (error) {
      logger.error('Error loading cached analytics:', error);
    }
    return null;
  };

  // Save analytics data to cache
  const saveCachedAnalytics = (websiteId: string, data: any) => {
    try {
      const cacheKey = getCacheKey(websiteId);
      const cacheData = {
        data,
        timestamp: Date.now()
      };
      sessionStorage.setItem(cacheKey, JSON.stringify(cacheData));
      // Analytics data cached
    } catch (error) {
      logger.error('Error saving cached analytics:', error);
    }
  };

  // Track if data has been loaded initially for current website
  const [loadedWebsiteId, setLoadedWebsiteId] = useState<string | null>(null);

  useEffect(() => {
    // Only load data if tab is active
    if (!isActive) return;
    
    // Reset loaded state if website changed
    if (website?.id !== loadedWebsiteId) {
      setLoadedWebsiteId(null);
    }
    
    if (website?.id && website.id !== loadedWebsiteId) {
      loadGoogleConnectionInfo();
      checkGoogleConnection();
      
      // Try to load from cache first, then fetch fresh data if needed
      const cachedData = loadCachedAnalytics(website.id);
      if (cachedData) {
        setAnalyticsData(cachedData.analyticsData);
        setChartData(cachedData.chartData);
        setSmoothChartData(cachedData.smoothChartData);
        setAvgSmoothChartData(cachedData.avgSmoothChartData);
        setIsSimulatedData(cachedData.isSimulatedData);
        setIsConnectedToGoogle(cachedData.isConnectedToGoogle);
        setLastRefreshTime(cachedData.timestamp);
        
        // If data is stale (older than 1 hour), refresh in background
        if (!isDataFresh(cachedData.timestamp)) {
          setTimeout(() => {
            loadAnalyticsData(website.id, website.domain, true);
          }, 1000); // Small delay to let UI render first
        }
      } else {
        // No cached data, load fresh data
        loadAnalyticsData(website.id, website.domain, false);
      }
      
      loadBlogPosts(website.id);
      loadSeoAudits(website.id);
      setLoadedWebsiteId(website.id);
    }
  }, [website?.id, isActive, loadedWebsiteId]);

  // Auto-refresh analytics data every 5 minutes to ensure we always have the latest 7 days
  // Only when tab is active
  useEffect(() => {
    if (!website || !isActive) return;

    const interval = setInterval(() => {
      loadAnalyticsData(website.id, website.domain, true);
    }, 5 * 60 * 1000); // 5 minutes

    return () => clearInterval(interval);
  }, [website, isActive]);

  useEffect(() => {
    if (seoAudits[0]) return;
    const interval = setInterval(() => {
      setStatusMessage((prev) => {
        const currentIndex = STATUS_MESSAGES.indexOf(prev);
        const nextIndex = (currentIndex + 1) % STATUS_MESSAGES.length;
        return STATUS_MESSAGES[nextIndex];
      });
    }, 2500);
    return () => clearInterval(interval);
  }, [seoAudits]);

  const timeElapsed = Math.floor((Date.now() - analysisStartTime) / 1000);
  const isOverTime = timeElapsed > 3600;

  const connectToGoogle = () => {
    navigate('/settings');
  };

  const processAnalyticsData = (data: any[], summary: any) => {
    // Sort data by date (oldest first) for proper comparison
    const sortedData = [...data]
      .filter((d) => !!d && typeof d.date === 'string')
      .map((d) => ({
        date: String(d.date).slice(0, 10),
        clicks: Number.isFinite(Number(d.clicks)) ? Number(d.clicks) : 0,
        impressions: Number.isFinite(Number(d.impressions)) ? Number(d.impressions) : 0,
        ctr: Number.isFinite(Number(d.ctr)) ? Number(d.ctr) : 0,
        avg_position: Number.isFinite(Number(d.avg_position)) ? Number(d.avg_position) : 0
      }))
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    // Debug logs removed for cleaner console

    // Calculate week-over-week comparison with improved logic
    let clicksChange = 0;
    let impressionsChange = 0;

    if (sortedData.length >= 14) {
      // Get the last 14 days of data
      const last14Days = sortedData.slice(-14);
      
      // Current week: last 7 days
      const currentWeek = last14Days.slice(-7);
      // Previous week: days 8-14 from the end
      const previousWeek = last14Days.slice(0, 7);

      const currentClicks = currentWeek.reduce((sum, day) => sum + (day.clicks || 0), 0);
      const previousClicks = previousWeek.reduce((sum, day) => sum + (day.clicks || 0), 0);
      const currentImpressions = currentWeek.reduce((sum, day) => sum + (day.impressions || 0), 0);
      const previousImpressions = previousWeek.reduce((sum, day) => sum + (day.impressions || 0), 0);

      // Calculate percentage changes with proper handling of edge cases
      clicksChange = previousClicks > 0 ? ((currentClicks - previousClicks) / previousClicks) * 100 : 
                    currentClicks > 0 ? 100 : 0; // If previous was 0 but current has data, show 100% increase
      impressionsChange = previousImpressions > 0 ? ((currentImpressions - previousImpressions) / previousImpressions) * 100 : 
                         currentImpressions > 0 ? 100 : 0;

      // Comparison computed
    } else if (sortedData.length >= 7) {
      // If we have less than 14 days but at least 7, compare with the first 7 days
      const currentWeek = sortedData.slice(-7);
      const previousWeek = sortedData.slice(0, 7);
      
      const currentClicks = currentWeek.reduce((sum, day) => sum + (day.clicks || 0), 0);
      const previousClicks = previousWeek.reduce((sum, day) => sum + (day.clicks || 0), 0);
      const currentImpressions = currentWeek.reduce((sum, day) => sum + (day.impressions || 0), 0);
      const previousImpressions = previousWeek.reduce((sum, day) => sum + (day.impressions || 0), 0);

      clicksChange = previousClicks > 0 ? ((currentClicks - previousClicks) / previousClicks) * 100 : 
                    currentClicks > 0 ? 100 : 0;
      impressionsChange = previousImpressions > 0 ? ((currentImpressions - previousImpressions) / previousImpressions) * 100 : 
                         currentImpressions > 0 ? 100 : 0;
    }

    // Create a complete 7-day dataset with real Google Search Console data (accounting for delay)
    const endDate = new Date();
    endDate.setDate(endDate.getDate() - 4); // 4 days ago (ensuring data availability)
    const chartData = [];
    
    // Generate the last 7 days of data that Google Search Console always has (10 days ago to 4 days ago)
    for (let i = 6; i >= 0; i--) {
      const targetDate = new Date(endDate);
      targetDate.setDate(endDate.getDate() - i);
      const dateString = targetDate.toISOString().split('T')[0];
      
      // Find matching data for this date
      const existingData = sortedData.find(day => day.date === dateString);
      
      if (existingData) {
        chartData.push({
          date: dateString,
          clicks: existingData.clicks || 0,
          impressions: existingData.impressions || 0,
          ctr: existingData.ctr || 0,
          position: existingData.avg_position || 0
        });
      } else {
        // Fill missing dates with zero values (real data, not mock)
        chartData.push({
          date: dateString,
          clicks: 0,
          impressions: 0,
          ctr: 0,
          position: 0
        });
      }
    }

    // Chart data prepared

    const finalAnalyticsData = {
      totalClicks: summary.totalClicks,
      totalImpressions: summary.totalImpressions,
      avgCtr: summary.avgCtr,
      avgPosition: summary.avgPosition,
      clicksChange: parseFloat(clicksChange.toFixed(1)),
      impressionsChange: parseFloat(impressionsChange.toFixed(1))
    };

    setAnalyticsData(finalAnalyticsData);
    setChartData(chartData);
    setSmoothChartData(chartData);
    setAvgSmoothChartData(chartData);

    // Cache the processed data
    if (website?.id) {
      saveCachedAnalytics(website.id, {
        analyticsData: finalAnalyticsData,
        chartData,
        smoothChartData: chartData,
        avgSmoothChartData: chartData,
        isSimulatedData,
        isConnectedToGoogle,
        timestamp: Date.now()
      });
    }
  };

  const checkGoogleConnection = async () => {
    try {
      if (!user?.id) {
        setIsConnectedToGoogle(false);
        return;
      }

      // First check if user has GSC tokens in settings
      const { data: settingsData, error: settingsError } = await supabase
        .from('user_settings')
        .select('google_search_console_access_token, google_search_console_refresh_token, google_search_console_email, google_search_console_site_url')
        .eq('user_id', user?.id)
        .single();

      if (!settingsError && settingsData?.google_search_console_access_token) {
        setIsConnectedToGoogle(true);
        
        // Also update connection info
        setGoogleConnectionInfo({
          email: settingsData.google_search_console_email,
          siteUrl: settingsData.google_search_console_site_url,
          connectedAt: settingsData.google_search_console_connected_at
        });
        return;
      }

      console.log('❌ No valid tokens found in settings');
      setIsConnectedToGoogle(false);
    } catch (error) {
      console.error('❌ Error checking Google connection:', error);
      setIsConnectedToGoogle(false);
    }
  };

  const loadAnalyticsData = async (websiteId: string, domain: string, forceRefresh = false) => {
    // Set a timeout to prevent infinite loading
    const timeoutId = setTimeout(() => {
      logger.error('Analytics data load timed out');
      setIsSimulatedData(true);
      setIsConnectedToGoogle(false);
    }, 30000); // 30 second timeout

    try {

      // Always fetch fresh data to ensure we get the latest 7 days
      // Only use cache if it's very recent (less than 5 minutes old) and not forcing refresh
      if (!forceRefresh) {
        const cachedData = loadCachedAnalytics(websiteId);
        const cacheAge = Date.now() - (cachedData?.timestamp || 0);
        const isRecentCache = cacheAge < 2 * 60 * 1000; // 2 minutes
        
        if (cachedData && isRecentCache) {
          setAnalyticsData(cachedData.analyticsData);
          setChartData(cachedData.chartData);
          setSmoothChartData(cachedData.smoothChartData);
          setAvgSmoothChartData(cachedData.avgSmoothChartData);
          setIsSimulatedData(cachedData.isSimulatedData);
          setIsConnectedToGoogle(cachedData.isConnectedToGoogle);
          setLastRefreshTime(cachedData.timestamp);
          return;
        }
      }

      // Always fetch fresh data from Google Search Console to ensure latest 7 days
      
      // First check if user has valid Google Search Console tokens in database
      const { data: settingsData, error: settingsError } = await supabase
        .from('user_settings')
        .select('google_search_console_access_token, google_search_console_refresh_token, google_search_console_email')
        .eq('user_id', user?.id)
        .single();

      if (settingsError || !settingsData?.google_search_console_access_token) {
        logger.error('No valid Google Search Console tokens found in database');
        setIsConnectedToGoogle(false);
        return;
      }

      // Get session for API call
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !sessionData?.session) {
        logger.error('No valid session found:', sessionError);
        setIsConnectedToGoogle(false);
        return;
      }
      
      const accessToken = sessionData.session.access_token;
      if (!accessToken) {
        logger.error('No access token found in session');
        setIsConnectedToGoogle(false);
        return;
      }
      
      const { data: freshData, error: fetchError } = await withRetry(
        () => supabase.functions.invoke('fetch-analytics', {
          body: { 
            websiteId: websiteId,
            domain: domain,
            startDate: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 10 days ago (4 days delay + 6 days back)
            endDate: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000).toISOString().split('T')[0] // 4 days ago (ensuring data availability)
          },
          headers: { Authorization: `Bearer ${accessToken}` }
        }),
        'fetch fresh analytics from Google Search Console'
      );

      if (fetchError) {
        logger.error('Error fetching fresh data:', fetchError);
        // Fallback to database data if fresh fetch fails
      }

      // Fetch data from database (either fresh or existing)
      const { data: existingData, error: dbError } = await withRetry(
        () => supabase
          .from('analytics_data')
          .select('*')
          .eq('website_id', websiteId)
          .order('date', { ascending: false })
          .limit(30),
        'fetch analytics_data from DB'
      );

      if (!dbError && existingData && existingData.length > 0) {

        // Separate real and simulated data
        const realData = existingData.filter(record => 
          record.data_source === 'google_search_console' || 
          record.data_source === 'google_search_console_real'
        );
        const simulatedData = existingData.filter(record => 
          record.data_source === 'simulated' || 
          record.data_source === 'mock' ||
          record.data_source === 'google_search_console_fallback' ||
          record.data_source === 'search_console_simulation'
        );

        // Prioritize real data if available
        if (realData.length > 0) {
          setIsSimulatedData(false);
          setIsConnectedToGoogle(true);
          
          // Calculate summary from real data - use last 7 days (excluding current day, matching Google Search Console)
          const last7Days = realData.slice(0, 7); // Get most recent 7 days (already excludes current day)
          const totalClicks = last7Days.reduce((sum, day) => sum + (day.clicks || 0), 0);
          const totalImpressions = last7Days.reduce((sum, day) => sum + (day.impressions || 0), 0);
          
          // Calculate CTR the same way Google Search Console does (total clicks / total impressions)
          const avgCtr = totalImpressions > 0 ? (totalClicks / totalImpressions * 100) : 0;
          
          // Calculate average position the same way Google Search Console does (weighted by impressions)
          let weightedPositionSum = 0;
          let totalImpressionsForPosition = 0;
          last7Days.forEach(day => {
            const impressions = day.impressions || 0;
            const position = parseFloat(String(day.avg_position)) || 0;
            weightedPositionSum += position * impressions;
            totalImpressionsForPosition += impressions;
          });
          const avgPosition = totalImpressionsForPosition > 0 ? weightedPositionSum / totalImpressionsForPosition : 0;

          const summary = {
            totalClicks,
            totalImpressions,
            avgCtr: parseFloat(avgCtr.toFixed(2)),
            avgPosition: parseFloat(avgPosition.toFixed(1))
          };

          // Process real data
          processAnalyticsData(realData, summary);
          return;
        } else {
          setIsSimulatedData(true);
          setIsConnectedToGoogle(false);
          
          // Calculate summary from simulated data
          const totalClicks = simulatedData.reduce((sum, day) => sum + (day.clicks || 0), 0);
          const totalImpressions = simulatedData.reduce((sum, day) => sum + (day.impressions || 0), 0);
          const avgCtr = totalImpressions > 0 ? (totalClicks / totalImpressions * 100) : 0;
          const avgPosition = simulatedData.reduce((sum, day) => sum + (parseFloat(String(day.avg_position)) || 0), 0) / simulatedData.length;

          const summary = {
            totalClicks,
            totalImpressions,
            avgCtr: parseFloat(avgCtr.toFixed(2)),
            avgPosition: parseFloat(avgPosition.toFixed(1))
          };

          // Process simulated data as fallback
          processAnalyticsData(simulatedData, summary);
          return;
        }
      }

      // If no database data, try edge function as last resort
      const { data: fallbackData, error: fallbackError } = await withRetry(
        () => supabase.functions.invoke('fetch-analytics', {
          body: {
            websiteId,
            domain,
            startDate: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 10 days ago (4 days delay + 6 days back)
            endDate: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000).toISOString().split('T')[0] // 4 days ago
          },
          headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : undefined
        }),
        'invoke fetch-analytics edge function'
      );

      if (!fallbackError && fallbackData.success && fallbackData.data && fallbackData.data.length > 0) {
        setIsSimulatedData(!!fallbackData.isSimulated);
        if (fallbackData.isSimulated === false) {
          setIsConnectedToGoogle(true);
        }

        processAnalyticsData(fallbackData.data, fallbackData.summary);
      } else {
        // Set empty data if no real data is available
        setAnalyticsData({
          totalClicks: 0,
          totalImpressions: 0,
          avgCtr: 0,
          avgPosition: 0,
          clicksChange: 0,
          impressionsChange: 0
        });
        setChartData([]);
        setSmoothChartData([]);
        setAvgSmoothChartData([]);
        setIsSimulatedData(false); // Not simulated, just no data
      }
    } catch (error) {
      logger.error('Error loading analytics data:', error);
      // Set empty data on error
      setAnalyticsData({
        totalClicks: 0,
        totalImpressions: 0,
        avgCtr: 0,
        avgPosition: 0,
        clicksChange: 0,
        impressionsChange: 0
      });
      setChartData([]);
      setSmoothChartData([]);
      setAvgSmoothChartData([]);
      setIsSimulatedData(false); // Not simulated, just error
    } finally {
      // Clear timeout
      clearTimeout(timeoutId);
    }
  };

  // Refresh data function
  const refreshData = async () => {
    if (!website) {
      return;
    }
    
    if (isRefreshing) {
      return;
    }

    setIsRefreshing(true);
    try {
      
      // Check if user is still authenticated before proceeding
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !sessionData?.session) {
        logger.error('User session expired during refresh:', sessionError);
        setIsRefreshing(false);
        return;
      }
      
      // First check connection status
      await checkGoogleConnection();
      
      // Clear only analytics-related caches, not session data
      const analyticsKeys = Object.keys(localStorage).filter(key => key.startsWith('analytics_'));
      analyticsKeys.forEach(key => localStorage.removeItem(key));
      
      const sessionAnalyticsKeys = Object.keys(sessionStorage).filter(key => key.startsWith('analytics_'));
      sessionAnalyticsKeys.forEach(key => sessionStorage.removeItem(key));
      
      await loadAnalyticsData(website.id, website.domain, true);
      setLastRefreshTime(Date.now());
      // Analytics data refreshed successfully
    } catch (error) {
      logger.error('Error refreshing analytics data:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const loadBlogPosts = async (websiteId: string) => {
    try {
      const { data, error } = await supabase
        .from('generated_articles')
        .select('*')
        .eq('website_id', websiteId)
        .order('created_at', { ascending: false })
        .limit(2);

      if (error) throw error;
      setBlogPosts(data || []);
    } catch (error) {
      logger.error('Error loading blog posts:', error);
    }
  };

  const loadSeoAudits = async (websiteId: string) => {
    try {
      const { data, error } = await supabase
        .from('seo_audits')
        .select('*')
        .eq('website_id', websiteId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setSeoAudits(data || []);
    } catch (error) {
      logger.error('Error loading SEO audits:', error);
    }
  };


  const runWebsiteAnalysis = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('analyze-website', {
        body: {
          websiteUrl: `https://${website.domain}`,
          websiteId: website.id
        }
      });

      if (error) throw error;
      if (data.success) {
        toast.success("Website analysis complete!");
        await loadSeoAudits(website.id);
      }
    } catch (error) {
      logger.error('Error analyzing website:', error);
      toast.error("Failed to analyze website");
    }
  };

  return (
    <div className="space-y-8">
      {/* Hero Section */}
      <HeroSection onGenerateClick={() => navigate('/my-content')} />

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="relative">
          <MetricCard
            title={
              <div className="flex items-center gap-2">
                <span>SEO Score</span>
                {seoScore && (
                  <TooltipProvider>
                    <Tooltip delayDuration={300}>
                      <TooltipTrigger asChild>
                        <Info className="h-3 w-3 text-gray-400 hover:text-gray-600 transition-colors duration-200 cursor-pointer" />
                      </TooltipTrigger>
                      <TooltipPrimitive.Portal>
                        <TooltipContent 
                          side="top" 
                          className="p-3 bg-white border shadow-xl rounded-md max-w-xs z-[99999] relative"
                          sideOffset={12}
                          avoidCollisions={true}
                          collisionPadding={40}
                          align="start"
                          sticky="always"
                          style={{ zIndex: 99999 }}
                        >
                        <div className="space-y-2">
                          <div className="text-xs font-medium text-gray-900 mb-2">SEO Breakdown</div>
                          <div className="grid grid-cols-2 gap-2 text-xs">
                            <div className="flex justify-between">
                              <span className="text-gray-600">Visibility:</span>
                              <span className={`font-medium ${getScoreColor(seoScore.breakdown.visibility.score)}`}>
                                {seoScore.breakdown.visibility.score}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Engagement:</span>
                              <span className={`font-medium ${getScoreColor(seoScore.breakdown.engagement.score)}`}>
                                {seoScore.breakdown.engagement.score}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Performance:</span>
                              <span className={`font-medium ${getScoreColor(seoScore.breakdown.performance.score)}`}>
                                {seoScore.breakdown.performance.score}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Technical:</span>
                              <span className={`font-medium ${getScoreColor(seoScore.breakdown.technical.score)}`}>
                                {seoScore.breakdown.technical.score}
                              </span>
                            </div>
                          </div>
                          
                          {seoScore.insights.length > 0 && (
                            <div className="pt-2 border-t border-gray-100">
                              <div className="text-xs text-green-600">
                                ✓ {seoScore.insights[0]}
                              </div>
                              {seoScore.insights[1] && (
                                <div className="text-xs text-green-600 mt-1">
                                  ✓ {seoScore.insights[1]}
                                </div>
                              )}
                            </div>
                          )}
                          
                          {seoScore.improvements.length > 0 && (
                            <div className="pt-2 border-t border-gray-100">
                              <div className="text-xs text-orange-600">
                                → {seoScore.improvements[0]}
                              </div>
                              {seoScore.improvements[1] && (
                                <div className="text-xs text-orange-600 mt-1">
                                  → {seoScore.improvements[1]}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                        </TooltipContent>
                      </TooltipPrimitive.Portal>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </div>
            }
            value={seoScoreLoading ? "..." : seoScore ? `${seoScore.overall}/100` : "N/A"}
            icon={<BarChart3 className="h-4 w-4" />}
            subtitle={
              seoScoreLoading ? "Calculating..." :
              seoScore ? "Overall performance" : "Connect Google Search Console to analyze"
            }
          />
        </div>
        
        <MetricCard
          title="Backlinks"
          value={backlinksLoading ? "..." : totalBacklinks.toLocaleString()}
          subtitle={
            backlinksLoading ? "Loading..." :
            totalBacklinks > 0 ? (
              <div className="flex items-center gap-2">
                <span className="text-green-600">{activeBacklinks} active</span>
                {pendingBacklinks > 0 && (
                  <span className="text-yellow-600">• {pendingBacklinks} pending</span>
                )}
                {failedBacklinks > 0 && (
                  <span className="text-red-600">• {failedBacklinks} failed</span>
                )}
                {last30Days > 0 && (
                  <span className="text-blue-600">• +{last30Days} this month</span>
                )}
              </div>
            ) : "No backlinks yet"
          }
          icon={<Link2 className="h-4 w-4" />}
        />
        
        <MetricCard
          title="Blog Posts"
          value={blogMetricsLoading ? "..." : totalGenerated.toString()}
          subtitle={
            blogMetricsLoading ? "Loading..." :
            totalGenerated > 0 ? "Generated articles" : "No articles generated yet"
          }
          icon={<FileText className="h-4 w-4" />}
        />
        
        <MetricCard
          title="Page Speed"
          value={pageSpeedLoading ? "..." : pageSpeedScore > 0 ? `${pageSpeedScore}/100` : "N/A"}
          subtitle={
            pageSpeedLoading ? "Analyzing..." : 
            pageSpeedScore > 0 ? `Grade: ${pageSpeedGrade}` : 
            "Connect Google Search Console to analyze"
          }
          icon={<TrendingUp className="h-4 w-4" />}
        />
      </div>


      {/* Performance Section */}
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h3 className="text-xl font-semibold text-gray-900 tracking-tight">Performance</h3>
          <Button
            onClick={refreshData}
            disabled={isRefreshing}
            variant="outline"
            className="gap-2 rounded-full px-6 py-3 text-base font-semibold border-2 border-border/50 bg-background/90 backdrop-blur-sm text-foreground hover:bg-primary hover:text-primary-foreground hover:border-primary transition-all duration-500 ease-in-out shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            {isRefreshing ? 'Refreshing...' : 'Refresh Data'}
          </Button>
        </div>
        {analyticsData && chartData.length > 0 ? (
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <div className={`w-2 h-2 rounded-full ${!isConnectedToGoogle ? 'bg-red-500' : isSimulatedData ? 'bg-yellow-500' : 'bg-green-500'}`}></div>
                <span>
                  {!isConnectedToGoogle
                    ? 'Not connected to Google Search Console'
                    : isSimulatedData
                    ? 'Showing realistic data (Google Search Console has no data yet for this site)'
                    : chartData && chartData.length > 0
                      ? `Google data from ${getDateRange(chartData)}`
                      : 'Loading Google data...'
                  }
                </span>
              </div>
              {googleConnectionInfo && isConnectedToGoogle && (
                <div className="text-xs text-muted-foreground">
                  Connected: {googleConnectionInfo.email} • {website?.domain}
                </div>
              )}
              {lastRefreshTime > 0 && (
                <div className="text-xs text-muted-foreground">
                  Updated {getTimeAgo(lastRefreshTime)}
                </div>
              )}
            </div>
            <div className="relative">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Debug log removed to prevent console spam */}
                <PerformanceChart
                  title="Clicks"
                  value={analyticsData.totalClicks}
                  change={analyticsData.clicksChange}
                  chartData={chartData}
                  gradientId="clicksGradientSoft"
                  strokeColor="#6A63D9"
                  stopColor="#7B6CF1"
                  hoverActive={hoverActive}
                  setHoverActive={setHoverActive}
                />
                <PerformanceChart
                  title="Impressions"
                  value={analyticsData.totalImpressions}
                  change={analyticsData.impressionsChange}
                  chartData={chartData}
                  gradientId="impressionsGradientSoft"
                  strokeColor="#E76E5B"
                  stopColor="#F88A77"
                  hoverActive={hoverActive}
                  setHoverActive={setHoverActive}
                />
              </div>

              {/* Premium blur overlay when not connected to Google Search Console - matching hero theme */}
              {!isConnectedToGoogle && (
                <div className="absolute inset-0 backdrop-blur-md bg-gradient-to-r from-primary/5 to-primary/10 rounded-2xl flex items-center justify-center">
                  <div className="bg-card rounded-2xl border border-primary/20 px-6 py-6 text-center shadow-xl max-w-md w-[90%] mx-auto"
                       style={{
                         boxShadow: '0 25px 60px -30px rgba(59, 130, 246, 0.15), 0 15px 30px -15px rgba(59, 130, 246, 0.1)'
                       }}>
                    <div className="text-base font-bold text-foreground mb-2">
                      Connect Google Search Console
                    </div>
                    <div className="text-sm text-muted-foreground mb-4 leading-relaxed">
                      Connect your Google Search Console account to view real-time performance data from your website
                    </div>
                    <Button
                      onClick={connectToGoogle}
                      className="rounded-xl px-5 py-2.5 text-sm font-semibold bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg hover:shadow-xl transition-all duration-200"
                    >
                      Connect Now
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="relative">
            {/* Mock data visible behind blur */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <PerformanceChart
                title="Clicks"
                value={analyticsData?.totalClicks || 0}
                change={analyticsData?.clicksChange || 0}
                chartData={chartData}
                gradientId="clicksGradientSoft"
                strokeColor="#6A63D9"
                stopColor="#7B6CF1"
                hoverActive={hoverActive}
                setHoverActive={setHoverActive}
              />
              <PerformanceChart
                title="Impressions"
                value={analyticsData?.totalImpressions || 0}
                change={analyticsData?.impressionsChange || 0}
                chartData={chartData}
                gradientId="impressionsGradientSoft"
                strokeColor="#E76E5B"
                stopColor="#F88A77"
                hoverActive={hoverActive}
                setHoverActive={setHoverActive}
              />
            </div>

            {/* Premium blur overlay - matching hero section theme */}
            <div className="absolute inset-0 backdrop-blur-md bg-gradient-to-r from-primary/5 to-primary/10 rounded-2xl flex items-center justify-center">
              <div className="bg-card rounded-3xl border border-primary/20 px-8 py-8 text-center shadow-2xl max-w-lg w-[95%] mx-auto"
                   style={{
                     boxShadow: '0 35px 80px -45px rgba(59, 130, 246, 0.2), 0 20px 40px -20px rgba(59, 130, 246, 0.15)'
                   }}>
                <div className="text-lg font-bold text-foreground mb-2">
                  {isConnectedToGoogle ? 'Loading Real Data...' : 'Connect Google Search Console'}
                </div>
                <div className="text-sm text-muted-foreground mb-6 leading-relaxed">
                  {isConnectedToGoogle
                    ? 'Fetching your latest performance data from Google Search Console'
                    : 'Connect your Google Search Console to see real performance data with automatic updates'
                  }
                </div>
                {!isConnectedToGoogle && (
                  <Button
                    onClick={connectToGoogle}
                    className="rounded-xl px-6 py-3 text-base font-semibold bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg hover:shadow-xl transition-all duration-200"
                  >
                    Connect Google Search Console
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Content Grid */}
      <ContentGrid 
        blogPosts={blogPosts}
        analyticsData={analyticsData} 
        isConnectedToGoogle={isConnectedToGoogle}
        connectToGoogle={connectToGoogle}
      />
    </div>
  );
}